import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, effect, inject, signal, untracked } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import type { DerivedKey, OnlineAccountView } from '../../application';
import { JourneyDashboard } from './journey-dashboard';
import { JourneyStore } from './journey.store';
import { PublicJourneyConfig, PublicJourneyUseCases } from './public-journey.dependencies';

/**
 * Where the online backup stands, as the screens say it: nothing to do, sending, waiting for the
 * private space to open, trying again after a failure, another phone's version in the way, or an
 * empty will about to go over one that is not.
 */
export type OnlineStatus =
  'idle' | 'sending' | 'waiting-locked' | 'retrying' | 'failed' | 'conflict' | 'confirm-empty';

/** Another phone wrote since this one last did: its revision, and when. */
export interface OnlineConflict {
  readonly currentRevision: number;
  readonly updatedAt: string;
}

export interface SendOptions {
  /** The owner said yes to an empty will going over one that is not. */
  readonly confirmEmpty?: boolean;
  /** The owner chose this phone's version over the one online. */
  readonly baseRevision?: number;
}

/** How long after the last change the will goes: long enough for a few edits to travel as one. */
export const SEND_DELAY_MILLISECONDS = 30_000;
/** After a failure, each try waits longer than the last, up to ten minutes. */
export const RETRY_DELAYS_MILLISECONDS: readonly number[] = [
  30_000, 60_000, 120_000, 300_000, 600_000,
];
/** Once the space opens again, what waited goes almost at once. */
const ON_OPEN_DELAY_MILLISECONDS = 1_000;

/**
 * The online backup, from the journey's side. It holds what the screens show — the account,
 * masked, and where the last send stands — and the one thing that must not outlive the open
 * session: the key derived for the account, dropped the moment the space closes.
 *
 * It sends by itself, thirty seconds after the last change, while the space is open; one send at a
 * time; again later, and later still, when the network or the server fails. Another phone's
 * version, or an empty will over one that is not, wait for the owner: nothing is overwritten
 * without them.
 *
 * Provided with the journey, so a send started on one screen finishes on the next. The use cases
 * behind it are fetched the first time the account is looked at.
 */
@Injectable()
export class JourneyOnline {
  private readonly store = inject(JourneyStore);
  private readonly dashboard = inject(JourneyDashboard);
  private readonly config = inject(PublicJourneyConfig);
  private readonly allUseCases = inject(PublicJourneyUseCases);
  private readonly useCases = this.allUseCases.online;
  private readonly i18n = inject(TranslocoService);
  private readonly window = inject(DOCUMENT).defaultView;

  /** Whether this build can reach i-dina-api at all. */
  readonly offered = this.config.onlineBackupOffered;
  /** The account this phone is signed in with, masked; null when there is none. */
  readonly account = signal<OnlineAccountView | null>(null);
  readonly status = signal<OnlineStatus>('idle');
  readonly conflict = signal<OnlineConflict | null>(null);
  /** Who has the wasi's access, kept with the journey (JourneyStore.wasiAccess). */
  readonly wasiAccess = this.store.wasiAccess;

  /** The key derived for the account in this session; it dies with the session. */
  private key: DerivedKey | null = null;
  /** Set when the phrase has changed: the next send draws a new salt. */
  private renewSalt = false;
  private sending: Promise<void> | null = null;
  private loading: Promise<void> | null = null;
  /** The will as it was last sent or taken back — its last change — so a send is only for news. */
  private sentDraftAt: string | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private retries = 0;

  constructor() {
    const destroyRef = inject(DestroyRef);
    this.store.whenLocked(() => {
      this.key = null;
      this.clearTimer();
      if (this.account() !== null && (this.pending() || this.status() === 'sending')) {
        this.status.set('waiting-locked');
      }
    }, destroyRef);

    // Every change to the will passes through the draft: each one starts the countdown again.
    effect(() => {
      const updatedAt = this.store.draft()?.updatedAt.value ?? null;
      const account = this.account();
      if (updatedAt === null || account === null) return;
      untracked(() => this.willChanged(updatedAt, account));
    });

    // The space opened again: what was waiting for it goes.
    effect(() => {
      this.store.sessionOpened();
      untracked(() => {
        if (this.account() === null || !this.pending()) return;
        if (this.status() === 'waiting-locked' || this.status() === 'idle') {
          this.schedule(ON_OPEN_DELAY_MILLISECONDS);
        }
      });
    });

    // The network came back: a send that was waiting for it goes now rather than later.
    const online = (): void => {
      if (this.status() === 'retrying') this.schedule(0);
    };
    this.window?.addEventListener('online', online);
    destroyRef.onDestroy(() => {
      this.clearTimer();
      this.window?.removeEventListener('online', online);
    });

    void this.load();
  }

  /** Reads the account from the phone, once; later calls wait for the same read. */
  load(): Promise<void> {
    if (!this.offered) return Promise.resolve();
    if (this.loading === null) {
      this.loading = this.readAccount();
    }
    return this.loading;
  }

  private async readAccount(): Promise<void> {
    try {
      this.account.set(await this.useCases.loadAccount.execute());
    } catch {
      this.account.set(null);
    }
    if (this.account() !== null) void this.readWasiAccess();
  }

  /** Asks the server, once the account is known, who has the wasi's access. */
  async readWasiAccess(): Promise<void> {
    try {
      const state = await (await this.allUseCases.wasi()).load.execute();
      if (state.status === 'named') this.wasiAccess.set(state.designation);
    } catch {
      // Unknown stays unknown: the screens then say nothing about it.
    }
  }

  /** Asks for a code by e-mail, in the language being read. */
  requestCode(email: string) {
    return this.useCases.requestCode.execute({ email, language: this.i18n.getActiveLang() });
  }

  /** Trades the code for a token; the account is then this phone's. */
  async verifyCode(email: string, code: string) {
    const result = await this.useCases.verifyCode.execute({
      email,
      code,
      deviceLabel: this.config.deviceLabel,
    });
    if (result.ok) {
      this.loading = Promise.resolve();
      this.account.set(result.value);
      // An account found again may already have given the access, from another phone.
      void this.readWasiAccess();
    }
    return result;
  }

  /**
   * Seals the will and sends it. One send at a time: a second asked for while one runs waits for
   * it and then goes, so the last change is the one that ends up online.
   */
  async send(options: SendOptions = {}): Promise<OnlineStatus> {
    if (this.account() === null) return this.status();
    this.clearTimer();
    while (this.sending !== null) await this.sending;
    const run = this.sendOnce(options);
    this.sending = run;
    try {
      await run;
    } finally {
      this.sending = null;
    }
    return this.status();
  }

  private async sendOnce(options: SendOptions): Promise<void> {
    const session = this.store.session;
    if (!session.isUnlocked) {
      this.status.set('waiting-locked');
      return;
    }
    this.status.set('sending');
    const draftAt = this.store.draft()?.updatedAt.value ?? null;
    const parts = this.dashboard.parts();
    try {
      const result = await this.useCases.send.execute({
        draftId: this.config.draftId,
        vault: session.requireVault(),
        encryption: session.encryption(this.config.keyDerivation),
        language: this.i18n.getActiveLang(),
        contentPack: this.config.contentPack,
        summary: {
          partsFilled: parts.filter((part) => part.done > 0).length,
          partsTotal: parts.length,
        },
        key: this.key,
        renewSalt: this.renewSalt,
        ...options,
      });
      switch (result.status) {
        case 'sent':
          // A session that closed while the request was out does not get its key back.
          this.key = session.isUnlocked ? result.key : null;
          this.renewSalt = false;
          this.retries = 0;
          this.sentDraftAt = draftAt;
          this.conflict.set(null);
          this.account.set(result.account);
          this.status.set('idle');
          // A change made while this one was out goes in its turn.
          if (this.pending()) this.schedule(SEND_DELAY_MILLISECONDS);
          return;
        case 'conflict':
          this.key = session.isUnlocked ? result.key : null;
          this.conflict.set({
            currentRevision: result.currentRevision,
            updatedAt: result.updatedAt,
          });
          this.status.set('conflict');
          return;
        case 'failed': {
          this.key = session.isUnlocked ? result.key : null;
          const transient = result.failure === 'offline' || result.failure === 'unavailable';
          this.status.set(transient ? 'retrying' : 'failed');
          if (transient) this.retryLater();
          return;
        }
        case 'signed-out':
          this.signedOut();
          return;
        case 'locked':
          this.status.set('waiting-locked');
          return;
        case 'confirm-empty':
          this.status.set('confirm-empty');
          return;
      }
    } catch {
      this.status.set('failed');
    }
  }

  /**
   * The will on this phone was just replaced by the one online: it is what is online now, so
   * there is nothing to send, and the account builds on the revision taken.
   */
  adopted(account: OnlineAccountView | null): void {
    this.sentDraftAt = this.store.draft()?.updatedAt.value ?? null;
    this.key = null;
    this.retries = 0;
    this.conflict.set(null);
    this.status.set('idle');
    if (account !== null) this.account.set(account);
  }

  /** This phone stops sending; the backup stays online. */
  async disconnect(): Promise<void> {
    await this.useCases.signOut.execute();
    this.signedOut();
  }

  /** Everything online goes, now: every revision, the address, every phone's token. */
  async deleteAccount(): Promise<boolean> {
    const result = await this.useCases.deleteAccount.execute();
    if (!('deleted' in result)) return false;
    this.signedOut();
    return true;
  }

  /** The phrase has just changed: the old key and salt are done with, and the will goes again. */
  phraseChanged(): void {
    this.key = null;
    this.renewSalt = true;
    if (this.account() !== null) void this.send();
  }

  /** Whether the will has changed since it last went online, or came from there. */
  private pending(): boolean {
    const updatedAt = this.store.draft()?.updatedAt.value ?? null;
    return updatedAt !== null && updatedAt !== this.sentDraftAt;
  }

  private willChanged(updatedAt: string, account: OnlineAccountView): void {
    if (this.sentDraftAt === null && account.sentAt !== null && updatedAt <= account.sentAt) {
      // The first look, after a restart: nothing has changed since the last send.
      this.sentDraftAt = updatedAt;
      return;
    }
    if (updatedAt === this.sentDraftAt) return;
    // Another phone's version, or an empty will: the owner decides, not a timer.
    if (this.status() === 'conflict' || this.status() === 'confirm-empty') return;
    if (!this.store.session.isUnlocked) {
      this.status.set('waiting-locked');
      return;
    }
    this.schedule(SEND_DELAY_MILLISECONDS);
  }

  private retryLater(): void {
    const delay =
      RETRY_DELAYS_MILLISECONDS[Math.min(this.retries, RETRY_DELAYS_MILLISECONDS.length - 1)];
    this.retries += 1;
    this.schedule(delay ?? SEND_DELAY_MILLISECONDS);
  }

  private schedule(delay: number): void {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      // A send already on its way (the owner pressed « send ») carries this change too.
      if (this.account() !== null && this.pending() && this.sending === null) void this.send();
    }, delay);
  }

  private clearTimer(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
  }

  private signedOut(): void {
    this.clearTimer();
    this.key = null;
    this.renewSalt = false;
    this.retries = 0;
    this.account.set(null);
    this.wasiAccess.set(undefined);
    this.conflict.set(null);
    this.status.set('idle');
  }
}
